const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

// Approximate only.
const HEADER_OFFSET = 48;
const VISIBILITY_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll("[data-media-index]"));
    this.thumbnails = Array.from(
      this.querySelectorAll("[data-thumbnail-index]"),
    );
    this.activeIndex = -1;
    this._setActive(0);

    this.desktopQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.desktopQuery.addEventListener("change", this._syncMode);
    this.addEventListener("click", this._onClick);

    this._syncMode();

    window.loadSwiper.then((swiperLib) => {
      this.swiperLib = swiperLib;
      this._syncMode();
    });
  }

  disconnectedCallback() {
    this.desktopQuery?.removeEventListener("change", this._syncMode);
    this.removeEventListener("click", this._onClick);
    this._stopObserver();
    this._destroyCarousel();
  }

  _syncMode = () => {
    if (this.desktopQuery.matches) {
      this._destroyCarousel();
      this._startObserver();
    } else {
      this._stopObserver();
      this._initCarousel();
    }
  };

  _onClick = (event) => {
    const thumbnail = event.target.closest("[data-thumbnail-index]");

    if (!thumbnail) return;

    this._scrollToMedia(Number(thumbnail.dataset.thumbnailIndex));
  };

  _initCarousel() {
    if (this.swiper || !this.swiperLib) return;

    const { Swiper, Pagination, Zoom } = this.swiperLib;
    const pagination = this.querySelector("[data-pagination]");

    this._toggleSwiperClasses(true);

    const params = {
      modules: [Pagination, Zoom],
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      zoom: { maxRatio: 5 },
      on: {
        slideChange: ({ activeIndex }) => this._setActive(activeIndex),
      },
    };

    if (pagination) {
      params.pagination = {
        el: pagination,
        clickable: true,
        renderBullet: (index, className) =>
          this._renderBullet(index, className),
      };
    }

    this.swiper = new Swiper(
      this.querySelector(".media-gallery__media"),
      params,
    );

    this._setActive(this.swiper.activeIndex);
  }

  _destroyCarousel() {
    if (!this.swiper) return;

    this.swiper.destroy(true, true);
    this.swiper = null;
    this._toggleSwiperClasses(false);
  }

  _toggleSwiperClasses(add) {
    this.querySelectorAll("[data-swiper-class]").forEach((el) =>
      el.classList.toggle(el.dataset.swiperClass, add),
    );
  }

  _renderBullet(index, className) {
    const isVideo = this.slides[index]?.dataset.mediaType === "video";
    const label = window.theme.translations(
      { label: this.dataset.slideLabel },
      "label",
      { index: index + 1, total: this.slides.length },
    );

    return `<button
      type="button"
      class="${className} media-gallery__bullet${isVideo ? " media-gallery__bullet--video" : ""}"
      aria-label="${label}"
    ></button>`;
  }

  _startObserver() {
    if (this.observer) return;

    this.visibleHeights = new Map();

    this.observer = new IntersectionObserver(this._onIntersect, {
      rootMargin: `-${HEADER_OFFSET}px 0px 0px 0px`,
      threshold: VISIBILITY_THRESHOLDS,
    });

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  _stopObserver() {
    if (!this.observer) return;

    this.observer.disconnect();
    this.observer = null;
  }

  _onIntersect = (entries) => {
    entries.forEach(({ target, intersectionRect, isIntersecting }) => {
      const index = Number(target.dataset.mediaIndex);

      if (isIntersecting) {
        this.visibleHeights.set(index, intersectionRect.height);
      } else {
        this.visibleHeights.delete(index);
      }
    });

    let activeIndex = this.activeIndex;
    let largest = 0;

    this.slides.forEach((_, index) => {
      const height = this.visibleHeights.get(index) || 0;

      if (height > largest) {
        largest = height;
        activeIndex = index;
      }
    });

    this._setActive(activeIndex);
  };

  _setActive(index) {
    if (index === this.activeIndex) return;

    this.activeIndex = index;

    this.thumbnails.forEach((thumbnail, i) => {
      const isActive = i === index;

      thumbnail.classList.toggle("media-gallery__thumbnail--active", isActive);

      if (isActive) {
        thumbnail.setAttribute("aria-current", "true");
      } else {
        thumbnail.removeAttribute("aria-current");
      }
    });

    this._playActiveVideo();
  }

  _playActiveVideo() {
    this.slides.forEach((slide, index) => {
      const video = slide.querySelector("video");

      if (!video) return;

      if (index === this.activeIndex) {
        video.muted = true;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  _scrollToMedia(index) {
    const slide = this.slides[index];

    if (!slide) return;

    if (this.swiper) {
      this.swiper.slideTo(index);
      return;
    }

    // `scroll-margin-top` on the slide keeps it clear of the sticky header.
    slide.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
